import type {
  BatchCoverageResponse,
  CoverageRowWire,
} from '~/features/catalog-operations/types/catalog-operations.api.types';
import type {
  BatchCoverage,
  CoverageAttributeRow,
  CoverageCategoryRow,
  CoverageDepthRow,
  CoverageDetailRow,
  ProductDetailState,
} from '~/features/catalog-operations/types/catalog-operations.types';

/**
 * Wire → domain mapping for the Enrichment Coverage signal cards. Sibling of
 * `~/utils/insights-transforms`; kept separate because the two endpoints mean
 * different things by the same column names.
 */

/** Reads an optional wire metric. Absent and null both mean "not computed",
 *  but every consumer here renders a number, so they collapse to 0 at exactly
 *  one place rather than being coerced ad hoc at each call site. */
function num(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Rows arrive ordered by rank from the API; this only guards against a
 *  payload that lost that order in transit, and never re-ranks. */
function byRank<T extends { rank: number }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => a.rank - b.rank);
}

/** Coverage-by-category rows. Only the "after" half exists upstream, so the
 *  before columns are deliberately not read. */
export function toCoverageCategories(rows: readonly CoverageRowWire[]): CoverageCategoryRow[] {
  return byRank(rows).map((r) => ({
    label: r.label,
    enriched: num(r.countAfter),
    percent: num(r.scoreAfter),
  }));
}

/** Coverage-by-attribute rows, flagging the impossible case. Upstream
 *  guarantees `after >= before`, so a violation means corrupt data rather than
 *  a real regression — the flag lets the card say so instead of silently
 *  drawing a shrinking bar. */
export function toCoverageAttributes(rows: readonly CoverageRowWire[]): CoverageAttributeRow[] {
  return byRank(rows).map((r) => {
    const beforeCount = num(r.countBefore);
    const afterCount = num(r.countAfter);
    return {
      label: r.label,
      beforeCount,
      afterCount,
      beforePercent: num(r.scoreBefore),
      afterPercent: num(r.scoreAfter),
      isRegression: afterCount < beforeCount,
    };
  });
}

/** Attribute-depth rows. `score*` here is average items per product, not a
 *  percentage, so it is mapped onto explicitly named fields — a shared
 *  "percent" field would invite a `%` suffix at render time. */
export function toCoverageDepth(rows: readonly CoverageRowWire[]): CoverageDepthRow[] {
  return byRank(rows).map((r) => {
    const itemsBefore = num(r.countBefore);
    const itemsAfter = num(r.countAfter);
    return {
      label: r.label,
      itemsBefore,
      itemsAfter,
      delta: itemsAfter - itemsBefore,
      avgPerProductBefore: num(r.scoreBefore),
      avgPerProductAfter: num(r.scoreAfter),
    };
  });
}

/** Product-detail change rows for one state. */
export function toCoverageDetailRows(rows: readonly CoverageRowWire[]): CoverageDetailRow[] {
  return byRank(rows).map((r) => ({
    label: r.label,
    products: num(r.countAfter),
    percent: num(r.scoreAfter),
  }));
}

/** Assembles the whole coverage payload. Every bucket is always present on the
 *  wire — the API serves empty arrays rather than omitting keys — so this reads
 *  them directly instead of defaulting. */
export function toBatchCoverage(wire: BatchCoverageResponse): BatchCoverage {
  const depth = toCoverageDepth(wire.ATTRIBUTE_DEPTH);
  const detail = wire.PRODUCT_DETAIL;
  const productDetail: Record<ProductDetailState, CoverageDetailRow[]> = {
    added: toCoverageDetailRows(detail.ADDED),
    changed: toCoverageDetailRows(detail.CHANGED),
    removed: toCoverageDetailRows(detail.REMOVED),
    renamed: toCoverageDetailRows(detail.RENAMED),
  };
  return {
    categories: toCoverageCategories(wire.COVERAGE_CATEGORY),
    attributes: toCoverageAttributes(wire.COVERAGE_ATTRIBUTE),
    depth,
    // Bars scale against the largest row rather than a fixed 100: depth is a
    // count of items, which has no natural ceiling. Floor of 1 keeps the
    // width maths safe on an empty set.
    depthMax: Math.max(1, ...depth.map((d) => d.itemsAfter)),
    productDetail,
  };
}

/** True when a coverage payload has no rows in any bucket — the shape the
 *  endpoint returns until the async coverage callback fills it. Shared so a
 *  caller's "still waiting" state and its poll always agree on what empty
 *  means. */
export function isBatchCoverageEmpty(coverage: BatchCoverage | undefined): boolean {
  if (!coverage) return true;
  return (
    coverage.categories.length === 0 &&
    coverage.attributes.length === 0 &&
    coverage.depth.length === 0 &&
    Object.values(coverage.productDetail).every((rows) => rows.length === 0)
  );
}
